import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  createArmature,
  deleteArmature,
  deleteArmatureImage,
  getArmatures,
  importArmatures,
  reorderArmatureImages,
  updateArmature,
  updateArmatureVisibility,
  uploadArmatureImage,
} from "../api/armaturesApi";
import { useAuth } from "../auth/AuthContext";
import {
  hasAnyPermission,
  hasPermission,
  isRootUser,
} from "../auth/permissions";
import DismissibleAlert from "../components/DismissibleAlert";
import DeleteButton from "../components/DeleteButton";
import EditButton from "../components/EditButton";
import ProductDeleteConfirmationDialog from "../components/ProductDeleteConfirmationDialog";
import RequiredFieldMarker from "../components/RequiredFieldMarker";
import {
  ProductCatalogActionButton,
  ProductCatalogPage,
  ProductCatalogSectionHeader,
  ProductCatalogTableFrame,
  ProductCatalogToolbar,
  productCatalogFlexibleTableClassName,
} from "../components/ProductCatalogOverview";
import ProductImageManager, {
  ProductImagePreview,
} from "../components/ProductImageManager";
import ProductCatalogDialog from "../components/ProductCatalogDialog";
import ProductCatalogColumnFilter from "../components/ProductCatalogColumnFilter";
import ProductCatalogTableControls from "../components/ProductCatalogTableControls";
import ProductCatalogSortableHeader from "../components/ProductCatalogSortableHeader";
import ProductChipListField from "../components/ProductChipListField";
import ProductCatalogEmptyRow from "../components/ProductCatalogEmptyRow";
import { PRODUCT_VISIBILITY_OPTIONS } from "../constants/productVisibility";
import { readProductCatalogTableSettings } from "../utils/productCatalogTableSettings";

const PRODUCT_PERMISSIONS = [
  "ARMATURES_READ",
  "ARMATURES_CREATE",
  "ARMATURES_UPDATE",
  "ARMATURES_DELETE",
];
const VISIBILITIES = PRODUCT_VISIBILITY_OPTIONS.map(({ value }) => value);
const ARMATURE_COLUMNS = [
  { key: "Model", label: "Model", getValue: (item) => item.model },
  { key: "Voltage", label: "Voltage", getValue: (item) => item.voltage },
  { key: "Power", label: "Power", getValue: (item) => item.power },
  {
    key: "Manufacturer",
    label: "Manufacturer",
    getValue: (item) => item.manufacturer,
  },
  { key: "OEM", label: "OEM", getValue: (item) => item.oems || [] },
  { key: "ORME", label: "ORME", getValue: (item) => item.orme || [] },
  { key: "WAI", label: "WAI", getValue: (item) => item.wai || "" },
  {
    key: "Dimensions",
    label: "Dimensions",
    getValue: (item) =>
      [
        ["OAL", item.oal],
        ["OD", item.od],
        ["Teeth", item.teeth],
        ["Slots", item.slots],
        ["DE shaft", item.deShaftDiameter],
        ["CE shaft", item.ceShaftDiameter],
      ]
        .filter(([, value]) => value != null)
        .map(([label, value]) => `${label}: ${value}`)
        .join(" "),
  },
  {
    key: "Images",
    label: "Images",
    getValue: (item) => item.images?.length || 0,
  },
];
const ARMATURE_COLUMN_WIDTHS = {
  Model: "min-w-28",
  Voltage: "min-w-16",
  Power: "min-w-16",
  Manufacturer: "min-w-28",
  OEM: "min-w-28",
  ORME: "min-w-24",
  WAI: "min-w-20",
  Dimensions: "min-w-36",
  Images: "min-w-20",
};
const ARMATURE_TABLE_SETTINGS_KEY = "armature-table-settings";
const readArmatureTableSettings = () =>
  readProductCatalogTableSettings(
    ARMATURE_TABLE_SETTINGS_KEY,
    ARMATURE_COLUMNS,
  );

const EMPTY_FORM = {
  model: "",
  voltage: "12",
  power: "1.2",
  oems: [],
  manufacturer: "",
  orme: [],
  wai: "",
  oal: "",
  od: "",
  teeth: "",
  slots: "",
  deShaftDiameter: "",
  ceShaftDiameter: "",
  visibility: "DRAFT",
};
const optionalNumber = (value) => (value === "" ? null : Number(value));
const createDraftImageId = () =>
  `draft-${
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }`;
const moveListItem = (items, index, offset) => {
  const targetIndex = index + offset;
  if (targetIndex < 0 || targetIndex >= items.length) return items;
  const reorderedItems = [...items];
  [reorderedItems[index], reorderedItems[targetIndex]] = [
    reorderedItems[targetIndex],
    reorderedItems[index],
  ];
  return reorderedItems;
};

export function requestFromForm(form) {
  return {
    model: form.model.trim(),
    voltage: form.voltage,
    power: form.power,
    oems: form.oems.map((entry) => entry.trim()).filter(Boolean),
    manufacturer: form.manufacturer.trim(),
    orme: form.orme.map((entry) => entry.trim()).filter(Boolean),
    wai: form.wai.trim() || null,
    oal: optionalNumber(form.oal),
    od: optionalNumber(form.od),
    teeth: optionalNumber(form.teeth),
    slots: optionalNumber(form.slots),
    deShaftDiameter: optionalNumber(form.deShaftDiameter),
    ceShaftDiameter: optionalNumber(form.ceShaftDiameter),
  };
}

export function requestFromImport(item) {
  if (!item || typeof item !== "object" || Array.isArray(item))
    throw new Error("Each imported armature must be a JSON object.");
  const read = (key, legacy) => item[key] ?? item[legacy];
  const asList = (value) =>
    Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
  const request = {
    model: read("model", "Model"),
    voltage: String(read("voltage", "Voltage") ?? ""),
    power: String(read("power", "Power") ?? ""),
    oems: asList(read("oems", "OEM")),
    manufacturer: read("manufacturer", "Manufacturer"),
    orme: asList(read("orme", "ORME")),
    wai: read("wai", "WAI") || null,
  };
  [
    ["oal", "OAL"],
    ["od", "OD"],
    ["teeth", "Teeth"],
    ["slots", "Slots"],
    ["deShaftDiameter", "DEShaftDiameter"],
    ["ceShaftDiameter", "CEShaftDiameter"],
  ].forEach(([key, legacy]) => {
    const value = read(key, legacy);
    request[key] =
      value === "" || value === undefined || value === null
        ? null
        : Number(value);
  });
  if (
    Object.values(request).some(
      (value) => typeof value === "number" && !Number.isFinite(value),
    )
  )
    throw new Error("Armature dimensions must be numbers.");
  return request;
}

function ArmatureForm({
  initial,
  isEditing,
  onCancel,
  onSave,
  saving,
  canManageVisibility,
  canUploadImages,
}) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [savedImages, setSavedImages] = useState(initial?.images || []);
  const [selectedImages, setSelectedImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [imageOrder, setImageOrder] = useState(
    (initial?.images || []).map((image) => image.imageId),
  );
  const [imageWarning, setImageWarning] = useState("");
  const [error, setError] = useState("");
  const updateField = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      await onSave(
        requestFromForm(form),
        form.visibility,
        { selectedImages, removedImageIds, imageOrder },
        {
          onRecordSaved: (record) => setSavedImages(record.images || []),
          onImageUploaded: (draftId, record, uploadedImage) => {
            setSavedImages(record.images || []);
            setSelectedImages((currentImages) =>
              currentImages.map((draftImage) =>
                draftImage.id === draftId
                  ? {
                      ...draftImage,
                      imageId: uploadedImage.imageId,
                      imageUrl: uploadedImage.imageUrl,
                    }
                  : draftImage,
              ),
            );
          },
        },
      );
    } catch (saveError) {
      setError(saveError.message || "Could not save this armature.");
    }
  };
  const textField = (name, label, options = {}) => (
    <label className="block text-sm font-medium text-zinc-700" key={name}>
      {label}
      {options.required && <RequiredFieldMarker />}
      <input
        name={name}
        value={form[name]}
        onChange={updateField}
        required={options.required}
        type={options.type || "text"}
        min={options.min}
        max={options.max}
        step={options.step}
        maxLength={options.maxLength}
        className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 font-normal focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
    </label>
  );
  const visibleSavedImages = savedImages.filter(
    (image) =>
      !removedImageIds.includes(image.imageId) &&
      !selectedImages.some(
        (draftImage) => draftImage.imageId === image.imageId,
      ),
  );
  const imageItems = [
    ...visibleSavedImages.map((image) => ({
      key: image.imageId,
      name: image.imageName,
      src: image.imageUrl,
      alt: `Armature ${form.model}`,
      status: "Saved",
      source: { type: "saved", image },
    })),
    ...selectedImages.map((draftImage) => ({
      key: draftImage.id,
      name: draftImage.file.name,
      src: draftImage.imageUrl,
      alt: `Armature ${form.model}`,
      status: draftImage.imageId ? "Uploaded" : "Ready to upload",
      source: { type: "draft", draftImage },
    })),
  ].sort(
    (firstImage, secondImage) =>
      imageOrder.indexOf(firstImage.key) - imageOrder.indexOf(secondImage.key),
  );
  const handleImageFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const visibleImageCount = visibleSavedImages.length + selectedImages.length;
    const availableSlots = Math.max(0, 5 - visibleImageCount);
    const acceptedFiles = selectedFiles.slice(0, availableSlots);
    const oversizedFiles = acceptedFiles.filter(
      (file) => file.size > 5 * 1024 * 1024,
    );
    const acceptedImages = acceptedFiles
      .filter((file) => !oversizedFiles.includes(file))
      .map((file) => ({ id: createDraftImageId(), file }));

    setSelectedImages((currentImages) => [...currentImages, ...acceptedImages]);
    setImageOrder((currentOrder) => [
      ...currentOrder,
      ...acceptedImages.map((image) => image.id),
    ]);
    setImageWarning(
      oversizedFiles.length || acceptedFiles.length < selectedFiles.length
        ? "Images must be PNG files up to 5 MB each, with up to five images total. Some selected files were not added."
        : "",
    );
    event.target.value = "";
  };
  return (
    <ProductCatalogDialog
      productName="Armature"
      title={isEditing ? `Edit armature ${form.model}` : "Create armature"}
      visibility={form.visibility}
      onClose={onCancel}
      closeDisabled={saving}
    >
      {error && (
        <DismissibleAlert onDismiss={() => setError("")}>
          {error}
        </DismissibleAlert>
      )}
      <form onSubmit={submit} className="space-y-5 p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {textField("model", "Model", { required: true, maxLength: 100 })}
          <label className="block text-sm font-medium text-zinc-700">
            Voltage
            <select
              name="voltage"
              value={form.voltage}
              onChange={updateField}
              className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 font-normal"
            >
              <option value="12">12 V</option>
              <option value="24">24 V</option>
            </select>
          </label>
          {textField("power", "Power", {
            required: true,
            type: "number",
            min: "0.1",
            max: "8.0",
            step: "0.1",
          })}
          {textField("manufacturer", "Manufacturer", {
            required: true,
            maxLength: 255,
          })}
          {textField("wai", "WAI", { maxLength: 255 })}
          {canManageVisibility && (
            <label className="block text-sm font-medium text-zinc-700">
              Visibility
              <select
                name="visibility"
                value={form.visibility}
                onChange={updateField}
                className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 font-normal"
              >
                {VISIBILITIES.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
          )}
          {[
            ["oal", "OAL"],
            ["od", "OD"],
            ["teeth", "Teeth"],
            ["slots", "Slots"],
            ["deShaftDiameter", "DE shaft diameter"],
            ["ceShaftDiameter", "CE shaft diameter"],
          ].map(([name, label]) =>
            textField(name, label, { type: "number", min: "1", step: "1" }),
          )}
        </div>
        <ProductChipListField
          id="armature-oems"
          label="OEM numbers"
          placeholder="Add an OEM number"
          values={form.oems}
          onChange={(oems) =>
            setForm((currentForm) => ({ ...currentForm, oems }))
          }
        />
        <ProductChipListField
          id="armature-orme"
          label="ORME numbers"
          placeholder="Add an ORME number"
          values={form.orme}
          onChange={(orme) =>
            setForm((currentForm) => ({ ...currentForm, orme }))
          }
        />
        {canUploadImages && (
          <>
            <ProductImageManager
              id="armature-image"
              images={imageItems}
              onFilesSelected={handleImageFiles}
              onMove={(imageIndex, offset) =>
                setImageOrder((currentOrder) =>
                  moveListItem(
                    currentOrder,
                    currentOrder.indexOf(imageItems[imageIndex].key),
                    offset,
                  ),
                )
              }
              onRemove={(productImage) => {
                const { type, image, draftImage } = productImage.source;
                if (type === "saved") {
                  setRemovedImageIds((currentIds) => [
                    ...currentIds,
                    image.imageId,
                  ]);
                } else {
                  if (draftImage.imageId) {
                    setRemovedImageIds((currentIds) =>
                      currentIds.includes(draftImage.imageId)
                        ? currentIds
                        : [...currentIds, draftImage.imageId],
                    );
                  }
                  setSelectedImages((currentImages) =>
                    currentImages.filter(
                      (currentImage) => currentImage.id !== draftImage.id,
                    ),
                  );
                }
                setImageOrder((currentOrder) =>
                  currentOrder.filter(
                    (imageKey) => imageKey !== productImage.key,
                  ),
                );
              }}
              disabled={saving}
            />
            {imageWarning && (
              <p role="alert" className="text-xs text-amber-800">
                {imageWarning}
              </p>
            )}
          </>
        )}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save armature"}
          </button>
        </div>
      </form>
    </ProductCatalogDialog>
  );
}

function ArmatureVisibilityChip({
  item,
  mayChangeVisibility,
  isOpen,
  pickerRef,
  dropdownRef,
  onToggle,
  onChange,
}) {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState(null);
  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 256;
      setPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: rect.bottom + 4,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);
  const option =
    PRODUCT_VISIBILITY_OPTIONS.find(({ value }) => value === item.visibility) ||
    PRODUCT_VISIBILITY_OPTIONS[0];

  return (
    <div
      ref={isOpen ? pickerRef : null}
      className={`relative flex flex-col items-start gap-1 ${
        isOpen ? "z-30" : ""
      }`}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Visibility: ${option.label} for ${item.model}${
          mayChangeVisibility ? ". Edit visibility" : ""
        }`}
        aria-haspopup={mayChangeVisibility ? "true" : undefined}
        aria-expanded={isOpen}
        onClick={onToggle}
        disabled={!mayChangeVisibility}
        title={option.description}
        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
          item.visibility === "PUBLISHED"
            ? "bg-green-100 text-green-800"
            : "bg-amber-100 text-amber-900"
        } ${
          mayChangeVisibility
            ? "cursor-pointer hover:ring-2 hover:ring-blue-300"
            : "cursor-default"
        }`}
      >
        {option.label}
      </button>
      {isOpen &&
        mayChangeVisibility &&
        position &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: position.left,
              top: position.top,
              maxHeight: "calc(100vh - 16px)",
              overflowY: "auto",
            }}
            className="z-[1000] w-64 space-y-1 rounded-md border border-zinc-300 bg-white p-2 shadow-lg"
            role="group"
            aria-label={`Choose visibility for ${item.model}`}
          >
            {PRODUCT_VISIBILITY_OPTIONS.map(({ value, label, description }) => (
              <button
                key={value}
                type="button"
                onClick={() => onChange(item, value)}
                disabled={item.visibility === value}
                className="block w-full rounded px-2 py-1 text-left hover:bg-zinc-100 disabled:bg-blue-50 disabled:font-semibold"
              >
                <span className="block text-xs text-zinc-900">{label}</span>
                <span className="block whitespace-normal text-[10px] font-normal text-zinc-600">
                  {description}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function ArmatureCatalogCell({
  item,
  columnKey,
  mayChangeVisibility,
  isVisibilityPickerOpen,
  visibilityPickerRef,
  visibilityDropdownRef,
  onToggleVisibility,
  onChangeVisibility,
}) {
  if (columnKey === "Model") {
    return (
      <div className="flex flex-col items-start gap-1">
        <span>{item.model}</span>
        <ArmatureVisibilityChip
          item={item}
          mayChangeVisibility={mayChangeVisibility}
          isOpen={isVisibilityPickerOpen}
          pickerRef={visibilityPickerRef}
          dropdownRef={visibilityDropdownRef}
          onToggle={onToggleVisibility}
          onChange={onChangeVisibility}
        />
      </div>
    );
  }
  if (columnKey === "Voltage")
    return item.voltage == null ? "—" : `${item.voltage} V`;
  if (columnKey === "Power") return item.power ?? "—";
  if (columnKey === "Manufacturer") return item.manufacturer || "—";
  if (columnKey === "OEM" || columnKey === "ORME") {
    const codes = columnKey === "OEM" ? item.oems : item.orme;
    return codes?.length ? (
      <ul className="flex min-w-0 flex-wrap gap-1">
        {codes.map((code, index) => (
          <li
            key={`${code}-${index}`}
            className="break-words rounded bg-gray-100 px-2 py-0.5 text-xs"
          >
            {code}
          </li>
        ))}
      </ul>
    ) : (
      <span className="text-gray-400">—</span>
    );
  }
  if (columnKey === "WAI") return item.wai || "—";
  if (columnKey === "Dimensions") {
    const dimensions = [
      ["OAL", item.oal],
      ["OD", item.od],
      ["Teeth", item.teeth],
      ["Slots", item.slots],
      ["DE shaft", item.deShaftDiameter],
      ["CE shaft", item.ceShaftDiameter],
    ].filter(([, value]) => value != null);
    return dimensions.length ? (
      <span className="text-xs text-zinc-600">
        {dimensions.map(([label, value]) => (
          <span key={label} className="mr-2 inline-block">
            {label}: {value}
          </span>
        ))}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );
  }
  if (columnKey === "Images") {
    return item.images?.length ? (
      <span className="flex items-center gap-1">
        {item.images.slice(0, 3).map((image) => (
          <ProductImagePreview
            key={image.imageId}
            src={image.imageUrl}
            alt={`Armature ${item.model}`}
          />
        ))}
        {item.images.length > 3 && (
          <span className="text-xs">+{item.images.length - 3}</span>
        )}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );
  }
  return null;
}

export default function ArmaturesPage() {
  const { user } = useAuth();
  const [savedTableSettings] = useState(readArmatureTableSettings);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState("ALL");
  const [columnOrder, setColumnOrder] = useState(
    savedTableSettings.columnOrder,
  );
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    savedTableSettings.visibleColumnKeys,
  );
  const [columnFilters, setColumnFilters] = useState(
    savedTableSettings.columnFilters,
  );
  const [sortConfig, setSortConfig] = useState(savedTableSettings.sortConfig);
  const [visibilityEditingId, setVisibilityEditingId] = useState(null);
  const visibilityPickerRef = useRef(null);
  const visibilityDropdownRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const confirmDeleteRef = useRef(null);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInput = useRef(null);
  const canRead = hasPermission(user, "ARMATURES_READ");
  const canCreate = hasPermission(user, "ARMATURES_CREATE");
  const canUpdate = hasPermission(user, "ARMATURES_UPDATE");
  const canDelete = hasPermission(user, "ARMATURES_DELETE");
  const canManageVisibility = hasPermission(
    user,
    "ARMATURES_VISIBILITY_MANAGE",
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    setLoadError("");
    try {
      setItems(await getArmatures());
    } catch (loadError) {
      setLoadError(loadError.message || "Could not load armatures.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (canRead) loadItems();
    else setLoading(false);
  }, [canRead, loadItems]);

  const visibleItems = useMemo(() => {
    const filteredItems = items.filter((item) => {
      if (visibilityFilter !== "ALL" && item.visibility !== visibilityFilter) {
        return false;
      }
      return ARMATURE_COLUMNS.every(({ key, getValue }) => {
        const filterValue = columnFilters[key] || "";
        if (!filterValue) return true;
        if (key === "Images")
          return filterValue === "has-images"
            ? Boolean(item.images?.length)
            : !item.images?.length;
        const columnValue = getValue(item);
        const searchableValue = Array.isArray(columnValue)
          ? columnValue.join(" ")
          : columnValue;
        return String(searchableValue ?? "")
          .toLowerCase()
          .includes(filterValue.trim().toLowerCase());
      });
    });
    const column = ARMATURE_COLUMNS.find(({ key }) => key === sortConfig.key);
    if (!column) return filteredItems;
    return [...filteredItems].sort((firstItem, secondItem) => {
      const firstValue = column.getValue(firstItem);
      const secondValue = column.getValue(secondItem);
      const comparison =
        typeof firstValue === "number" && typeof secondValue === "number"
          ? firstValue - secondValue
          : String(
              Array.isArray(firstValue)
                ? firstValue.join(" ")
                : firstValue ?? "",
            ).localeCompare(
              String(
                Array.isArray(secondValue)
                  ? secondValue.join(" ")
                  : secondValue ?? "",
              ),
              undefined,
              { numeric: true, sensitivity: "base" },
            );
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [columnFilters, items, sortConfig, visibilityFilter]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        ARMATURE_TABLE_SETTINGS_KEY,
        JSON.stringify({
          columnOrder,
          visibleColumnKeys,
          columnFilters,
          sortConfig,
        }),
      );
    } catch {
      // Keep the table usable when browser storage is unavailable.
    }
  }, [columnFilters, columnOrder, sortConfig, visibleColumnKeys]);

  const orderedColumns = columnOrder
    .filter((key) => visibleColumnKeys.includes(key))
    .map((key) => ARMATURE_COLUMNS.find((column) => column.key === key));

  const sortByColumn = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const reorderColumns = (event, targetKey) => {
    event.preventDefault();
    const sourceKey = event.dataTransfer.getData("text/plain");
    if (!sourceKey || sourceKey === targetKey) return;
    setColumnOrder((currentOrder) => {
      const nextOrder = [...currentOrder];
      const sourceIndex = nextOrder.indexOf(sourceKey);
      const targetIndex = nextOrder.indexOf(targetKey);
      if (sourceIndex < 0 || targetIndex < 0) return currentOrder;
      nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, sourceKey);
      return nextOrder;
    });
  };

  useEffect(() => {
    if (visibilityEditingId === null) return undefined;
    const closeOnOutsidePointerDown = (event) => {
      if (
        !visibilityPickerRef.current?.contains(event.target) &&
        !visibilityDropdownRef.current?.contains(event.target)
      ) {
        setVisibilityEditingId(null);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [visibilityEditingId]);

  const beginEdit = (item) =>
    setEditing({
      item,
      form: {
        model: item.model || "",
        voltage: String(item.voltage || "12"),
        power: String(item.power || "1.2"),
        oems: [...(item.oems || [])],
        manufacturer: item.manufacturer || "",
        orme: [...(item.orme || [])],
        wai: item.wai || "",
        oal: item.oal ?? "",
        od: item.od ?? "",
        teeth: item.teeth ?? "",
        slots: item.slots ?? "",
        deShaftDiameter: item.deShaftDiameter ?? "",
        ceShaftDiameter: item.ceShaftDiameter ?? "",
        visibility: item.visibility || "DRAFT",
      },
    });

  const saveItem = async (request, visibility, imageChanges, progress) => {
    setSaving(true);
    setError("");
    try {
      let current = editing?.item
        ? await updateArmature(editing.item.armatureId, request)
        : await createArmature(request);
      const isUpdating = Boolean(editing?.item);
      const saveKind = isUpdating ? "updated" : "created";
      const updateSavedRecord = (updated) => {
        current = updated;
        setItems((previous) =>
          [
            ...previous.filter(
              (item) => item.armatureId !== updated.armatureId,
            ),
            updated,
          ].sort((left, right) => left.model.localeCompare(right.model)),
        );
        progress.onRecordSaved(updated);
        setEditing((currentEditing) =>
          currentEditing
            ? { ...currentEditing, item: updated }
            : currentEditing,
        );
      };
      updateSavedRecord(current);

      for (const imageId of imageChanges.removedImageIds) {
        if (!current.images?.some((image) => image.imageId === imageId))
          continue;
        updateSavedRecord(
          await deleteArmatureImage(current.armatureId, imageId),
        );
      }

      const uploadedImageIdsByDraftId = new Map();
      for (const draftImage of imageChanges.selectedImages) {
        if (
          draftImage.imageId &&
          current.images?.some((image) => image.imageId === draftImage.imageId)
        ) {
          uploadedImageIdsByDraftId.set(draftImage.id, draftImage.imageId);
          continue;
        }
        const previousImageIds = new Set(
          (current.images || []).map((image) => image.imageId),
        );
        const uploadedRecord = await uploadArmatureImage(
          current.armatureId,
          draftImage.file,
        );
        const uploadedImage = uploadedRecord.images?.find(
          (image) => !previousImageIds.has(image.imageId),
        );
        updateSavedRecord(uploadedRecord);
        if (uploadedImage) {
          uploadedImageIdsByDraftId.set(draftImage.id, uploadedImage.imageId);
          progress.onImageUploaded(
            draftImage.id,
            uploadedRecord,
            uploadedImage,
          );
        }
      }

      const desiredImageIds = imageChanges.imageOrder
        .map((imageKey) => {
          if (current.images?.some((image) => image.imageId === imageKey)) {
            return imageKey;
          }
          return (
            uploadedImageIdsByDraftId.get(imageKey) ||
            imageChanges.selectedImages.find(
              (draftImage) => draftImage.id === imageKey,
            )?.imageId
          );
        })
        .filter(
          (imageId, index, imageIds) =>
            imageId &&
            current.images?.some((image) => image.imageId === imageId) &&
            imageIds.indexOf(imageId) === index,
        );
      const completeImageOrder = [
        ...desiredImageIds,
        ...(current.images || [])
          .map((image) => image.imageId)
          .filter((imageId) => !desiredImageIds.includes(imageId)),
      ];
      const savedImageOrder = (current.images || []).map(
        (image) => image.imageId,
      );
      if (
        completeImageOrder.length > 1 &&
        completeImageOrder.some(
          (imageId, index) => imageId !== savedImageOrder[index],
        )
      ) {
        updateSavedRecord(
          await reorderArmatureImages(current.armatureId, completeImageOrder),
        );
      }

      const originalVisibility =
        editing?.item?.visibility || current.visibility;
      if (visibility !== originalVisibility) {
        updateSavedRecord(
          await updateArmatureVisibility(current.armatureId, visibility),
        );
      }
      setNotice(`Armature ${saveKind}.`);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const changeVisibility = async (item, visibility) => {
    try {
      const updated = await updateArmatureVisibility(
        item.armatureId,
        visibility,
      );
      setItems((previous) =>
        previous.map((candidate) =>
          candidate.armatureId === item.armatureId ? updated : candidate,
        ),
      );
      setNotice(`Visibility changed to ${visibility}.`);
      setVisibilityEditingId(null);
    } catch (visibilityError) {
      setError(visibilityError.message || "Could not change visibility.");
    }
  };

  const removeItem = (item) => {
    setDeleteError("");
    setPendingDelete(item);
  };

  const confirmDeleteItem = async () => {
    if (!pendingDelete || deletingItemId) return;
    setDeletingItemId(pendingDelete.armatureId);
    setDeleteError("");
    try {
      await deleteArmature(pendingDelete.armatureId);
      setItems((previous) =>
        previous.filter(
          (candidate) => candidate.armatureId !== pendingDelete.armatureId,
        ),
      );
      setPendingDelete(null);
      setNotice("Armature deleted.");
    } catch (deleteError) {
      setDeleteError(deleteError.message || "Could not delete armature.");
    } finally {
      setDeletingItemId(null);
    }
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed))
        throw new Error("The import file must contain a JSON array.");
      const requests = parsed.map(requestFromImport);
      const result = await importArmatures(requests);
      if (canRead) await loadItems();
      const createdCount = result.created?.length ?? result.length ?? 0;
      const skippedCount = result.skippedModels?.length || 0;
      setNotice(
        `Import complete: ${createdCount} added${
          skippedCount
            ? `, ${skippedCount} already existed and were skipped`
            : ""
        }.`,
      );
    } catch (importError) {
      setError(importError.message || "Could not import armatures.");
    } finally {
      setImporting(false);
    }
  };

  const exportItems = () => {
    const exportRows = items.map((item) => ({
      imageUrl: item.images?.[0]?.imageUrl || "",
      Model: item.model,
      Voltage: item.voltage,
      Power: item.power,
      OEM: item.oems || [],
      Manufacturer: item.manufacturer,
      ORME: item.orme || [],
      WAI: item.wai || "",
      OAL: item.oal,
      OD: item.od,
      Teeth: item.teeth,
      Slots: item.slots,
      DEShaftDiameter: item.deShaftDiameter,
      CEShaftDiameter: item.ceShaftDiameter,
    }));
    const blobUrl = URL.createObjectURL(
      new Blob([JSON.stringify(exportRows, null, 2)], {
        type: "application/json",
      }),
    );
    const downloadLink = document.createElement("a");
    downloadLink.href = blobUrl;
    downloadLink.download = `armatures_${new Date()
      .toISOString()
      .slice(0, 10)}.json`;
    downloadLink.click();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  };

  if (!hasAnyPermission(user, PRODUCT_PERMISSIONS))
    return (
      <ProductCatalogPage
        title="Armatures"
        description="Search, review, and maintain product specifications."
      >
        <p className="mt-3 text-zinc-600">
          You don’t have permission to view or manage armatures.
        </p>
      </ProductCatalogPage>
    );

  return (
    <ProductCatalogPage
      title="Armatures"
      description="Search, review, and maintain product specifications."
    >
      {(canRead || canCreate) && (
        <ProductCatalogToolbar>
          {canCreate && (
            <ProductCatalogActionButton
              variant="primary"
              label="Add armature"
              title="Add armature"
              onClick={() => setEditing({ item: null, form: EMPTY_FORM })}
              disabled={loading || Boolean(loadError) || saving}
              icon="/icons/create-icon.svg"
            >
              Add armature
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && canRead && (
            <ProductCatalogActionButton
              label="Download data"
              title="Download data"
              onClick={exportItems}
              disabled={!items.length}
              icon="/icons/download-icon.svg"
            >
              Export data
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && canCreate && (
            <ProductCatalogActionButton
              label="Import JSON"
              title="Import JSON"
              onClick={() => fileInput.current?.click()}
              disabled={importing || loading}
              icon="/icons/import-icon.svg"
            >
              {importing ? "Importing…" : "Import JSON"}
            </ProductCatalogActionButton>
          )}
          {isRootUser(user) && canCreate && (
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={importFile}
            />
          )}
        </ProductCatalogToolbar>
      )}
      {error && (
        <DismissibleAlert onDismiss={() => setError("")}>
          {error}
        </DismissibleAlert>
      )}
      {notice && (
        <DismissibleAlert variant="success" onDismiss={() => setNotice("")}>
          {notice}
        </DismissibleAlert>
      )}
      {loadError && !loading && (
        <div role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">
          <p>Could not load armatures: {loadError}</p>
          <button
            type="button"
            className="mt-2 rounded bg-red-800 px-3 py-1 text-white"
            onClick={loadItems}
          >
            Try again
          </button>
        </div>
      )}
      {!canRead ? (
        <p className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-600">
          You can add armatures, but need the View armatures permission to
          browse existing records.
        </p>
      ) : (
        <>
          {!loading && !loadError && (
            <ProductCatalogSectionHeader
              description="Filter in the column headers; select a header to sort."
              controls={
                <>
                  <ProductCatalogTableControls
                    visibilityFilter={visibilityFilter}
                    onVisibilityFilterChange={setVisibilityFilter}
                    onReset={() => {
                      setColumnFilters({});
                      setVisibilityFilter("ALL");
                      setSortConfig({ key: "Model", direction: "asc" });
                    }}
                    columns={ARMATURE_COLUMNS}
                    visibleColumnKeys={visibleColumnKeys}
                    onVisibleColumnKeysChange={setVisibleColumnKeys}
                  />
                </>
              }
            />
          )}
          {loading ? (
            <p role="status">Loading armatures…</p>
          ) : loadError ? null : (
            <ProductCatalogTableFrame label="Armature list">
              <table className={productCatalogFlexibleTableClassName}>
                <thead className="bg-gray-50">
                  <tr>
                    {orderedColumns.map(({ key, label }) => (
                      <ProductCatalogSortableHeader
                        key={key}
                        columnKey={key}
                        label={label}
                        sortConfig={sortConfig}
                        onSort={sortByColumn}
                        widthClass={ARMATURE_COLUMN_WIDTHS[key]}
                        onDropColumn={reorderColumns}
                        filterId={`armature-filter-${key}`}
                      >
                        <ProductCatalogColumnFilter
                          id={`armature-filter-${key}`}
                          label={label}
                          value={columnFilters[key] || ""}
                          onChange={(value) =>
                            setColumnFilters((current) => ({
                              ...current,
                              [key]: value,
                            }))
                          }
                          options={
                            key === "Images"
                              ? [
                                  { value: "has-images", label: "Has images" },
                                  { value: "no-images", label: "No images" },
                                ]
                              : undefined
                          }
                        />
                      </ProductCatalogSortableHeader>
                    ))}
                    <th
                      scope="col"
                      className="border-b border-gray-200 px-2 py-2 text-left align-top"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visibleItems.length === 0 && (
                    <ProductCatalogEmptyRow colSpan={orderedColumns.length + 1}>
                      {items.length === 0
                        ? "No armatures are available yet."
                        : "No armatures match your filters."}
                    </ProductCatalogEmptyRow>
                  )}
                  {visibleItems.map((item) => {
                    const ownsItem = item.createdByUserId === user?.userId;
                    const mayChangeVisibility =
                      canManageVisibility || (ownsItem && canUpdate);
                    return (
                      <tr
                        key={item.armatureId}
                        className="align-top hover:bg-gray-50"
                      >
                        {orderedColumns.map(({ key }) => (
                          <td
                            key={key}
                            className="break-words px-2 py-3 text-gray-800"
                          >
                            <ArmatureCatalogCell
                              item={item}
                              columnKey={key}
                              mayChangeVisibility={mayChangeVisibility}
                              isVisibilityPickerOpen={
                                visibilityEditingId === item.armatureId
                              }
                              visibilityPickerRef={visibilityPickerRef}
                              visibilityDropdownRef={visibilityDropdownRef}
                              onToggleVisibility={() =>
                                mayChangeVisibility &&
                                setVisibilityEditingId((current) =>
                                  current === item.armatureId
                                    ? null
                                    : item.armatureId,
                                )
                              }
                              onChangeVisibility={changeVisibility}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-3">
                          <div className="flex gap-1">
                            {canUpdate && (
                              <EditButton
                                onClick={() => beginEdit(item)}
                                label={`Edit armature ${item.model}`}
                                title="Edit armature"
                              />
                            )}
                            {canDelete && (
                              <DeleteButton
                                onClick={() => removeItem(item)}
                                label={`Delete armature ${item.model}`}
                                title="Delete armature"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ProductCatalogTableFrame>
          )}
        </>
      )}
      {editing && (
        <ArmatureForm
          initial={{ ...editing.form, images: editing.item?.images || [] }}
          isEditing={Boolean(editing.item)}
          onCancel={() => setEditing(null)}
          onSave={saveItem}
          saving={saving}
          canManageVisibility={
            canManageVisibility ||
            (canUpdate &&
              (!editing.item || editing.item.createdByUserId === user?.userId))
          }
          canUploadImages={canUpdate}
        />
      )}
      {pendingDelete && (
        <ProductDeleteConfirmationDialog
          productName="armature"
          model={pendingDelete.model}
          error={deleteError}
          deleting={deletingItemId !== null}
          onCancel={() => {
            setPendingDelete(null);
            setDeleteError("");
          }}
          onConfirm={confirmDeleteItem}
          confirmButtonRef={confirmDeleteRef}
        />
      )}
    </ProductCatalogPage>
  );
}
